import { Card, Group, Skeleton, Stack, Text, ThemeIcon } from "@mantine/core";
import type { ReactNode } from "react";
import { semanticRoles, type SemanticRole } from "@how/design-system";

export interface StatCardProps {
  readonly label: string;
  /** Formatted value; pass `loading` instead while fetching. */
  readonly value: ReactNode;
  /** Secondary metadata under the value, for example a definition or a timestamp. */
  readonly hint?: string;
  /** A `DeltaValue`, a `StatusBadge`, or another small element that qualifies the metric. */
  readonly trailing?: ReactNode;
  readonly icon?: ReactNode;
  /** Semantic role for the icon tile. Information-encoding only; leave it neutral when the metric is neutral. */
  readonly role?: SemanticRole;
  readonly loading?: boolean;
  /** Hero treatment for the single most important metric on a dashboard: more air, larger radius and value. */
  readonly emphasis?: "standard" | "hero";
}

/**
 * One metric on a panel surface: value first by weight, label second, metadata third. Values carry tabular figures so
 * a row of metrics aligns (docs/UI_STANDARDS.md).
 */
export function StatCard({
  label,
  value,
  hint,
  trailing,
  icon,
  role = "neutral",
  loading = false,
  emphasis = "standard",
}: StatCardProps) {
  const hero = emphasis === "hero";
  return (
    <Card
      padding={hero ? "lg" : "md"}
      radius={hero ? "xl" : "md"}
      className={hero ? "how-panel how-panel--hero" : "how-panel"}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
        <Stack gap={hero ? 6 : 4} style={{ minWidth: 0 }}>
          <Text size="sm" c="dimmed" fw={500} truncate>
            {label}
          </Text>
          {loading ? (
            <Skeleton height={hero ? 40 : 30} width={110} data-testid="stat-skeleton" />
          ) : (
            <Text className="how-metric-value" fz={hero ? 34 : 26}>
              {value}
            </Text>
          )}
          {trailing}
          {hint !== undefined && (
            <Text size="xs" c="dimmed">
              {hint}
            </Text>
          )}
        </Stack>
        {icon !== undefined && (
          <ThemeIcon
            variant="light"
            color={semanticRoles[role]}
            size={hero ? "xl" : "lg"}
            radius="md"
            aria-hidden
          >
            {icon}
          </ThemeIcon>
        )}
      </Group>
    </Card>
  );
}
